import { forwardRef, type HTMLAttributes, type ReactNode, useImperativeHandle, useRef } from 'react'

import { useApplyCssVars, type CssVars } from '@/hooks/useCssVars'
import { cn } from '@/lib/utils'

export interface PrintCourtStoneChipProps {
  value: number | string
  className?: string
}

export function PrintCourtStoneChip({ value, className }: PrintCourtStoneChipProps) {
  return (
    <span className={cn('hs-gamekit hs-print-chip', className)}>
      {value}
    </span>
  )
}

export interface PrintDottedWriteLineProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  color?: string
}

export function PrintDottedWriteLine({
  className,
  color = 'rgba(27,26,64,0.36)',
  ...props
}: PrintDottedWriteLineProps) {
  return (
    <span
      className={cn('hs-gamekit hs-print-dotted-line', lineColorClass(color), className)}
      {...props}
    />
  )
}

export interface PrintWordBankStripProps {
  children: ReactNode
  title?: string
  className?: string
}

export function PrintWordBankStrip({
  children,
  title = 'WORD BANK',
  className,
}: PrintWordBankStripProps) {
  return (
    <section className={cn('hs-gamekit hs-print-word-bank', className)}>
      <span className="hs-print-word-bank__tab">{title}</span>
      <div className="hs-print-word-bank__body">{children}</div>
    </section>
  )
}

export interface PrintSunProps {
  className?: string
  color?: string
}

export function PrintSun({
  className,
  color = 'var(--print-accent, var(--yellow))',
}: PrintSunProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={cn('hs-gamekit hs-print-sun', printColorClass(color), className)}
    >
      <circle cx="32" cy="32" r="12" fill="currentColor" />
      {Array.from({ length: 8 }).map((_, index) => {
        const angle = (index / 8) * Math.PI * 2
        const x1 = 32 + Math.cos(angle) * 18
        const y1 = 32 + Math.sin(angle) * 18
        const x2 = 32 + Math.cos(angle) * 24
        const y2 = 32 + Math.sin(angle) * 24

        return (
          <line
            key={index}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        )
      })}
    </svg>
  )
}

export interface PrintFitContentProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  scale: number
}

export const PrintFitContent = forwardRef<HTMLDivElement, PrintFitContentProps>(
  function PrintFitContent({ className, scale, ...props }, forwardedRef) {
    const innerRef = useRef<HTMLDivElement | null>(null)
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLDivElement)
    useApplyCssVars(innerRef, { '--print-fit-scale': scale })

    return <div ref={innerRef} className={cn('hs-print-fit-content', className)} {...props} />
  },
)

export interface PrintCssVarSpanProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  cssVars?: CssVars
}

export function PrintCssVarSpan({ className, cssVars, ...props }: PrintCssVarSpanProps) {
  const spanRef = useRef<HTMLSpanElement | null>(null)
  useApplyCssVars(spanRef, cssVars)

  return <span ref={spanRef} className={className} {...props} />
}

function lineColorClass(color: string) {
  return color.includes('ink') ? 'is-line-ink' : 'is-line-muted'
}

function printColorClass(color: string) {
  if (color.includes('ink')) return 'is-ink'
  if (color.includes('coral')) return 'is-coral'
  if (color.includes('blue')) return 'is-blue'
  if (color.includes('green')) return 'is-green'
  if (color.includes('violet')) return 'is-violet'
  return 'is-yellow'
}
